import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Store, CheckCircle2, Upload } from "lucide-react";
import CityAutocomplete from "@/components/CityAutocomplete";

const CATEGORIES = ["Bahan Pangan", "Sayur & Buah", "Daging & Ikan", "Sembako", "Frozen Food", "Minuman", "Kemasan & Kantong", "Alat Dapur", "Lainnya"];

export default function SellerRegister() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", shop_name: "", city: "", category: "" });
  const [ktp, setKtp] = useState(null);
  const [nib, setNib] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["name","email","phone","shop_name","city","category"]) {
      if (!form[k]) { toast.error(`Kolom ${k} wajib diisi`); return; }
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => fd.append(k, v));
      if (ktp) fd.append("ktp", ktp);
      if (nib) fd.append("nib", nib);
      const r = await fetch(`${API}/seller/register`, { method: "POST", body: fd });
      if (!r.ok) throw new Error((await r.json()).detail || "Gagal");
      toast.success("Pendaftaran seller dikirim");
      setDone(true);
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  if (done) return (
    <div className="tech-grid min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <Card className="glass glow-border bg-transparent rounded-2xl p-8 w-full max-w-md text-center fade-up">
        <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4"/>
        <h1 className="font-display text-2xl font-bold text-white">Pendaftaran Terkirim</h1>
        <p className="text-slate-400 mt-2">Tim admin akan meninjau data & dokumen Anda dalam 1×24 jam. Anda akan dihubungi via WhatsApp setelah disetujui.</p>
        <div className="pill-cyan mx-auto w-fit mt-4">STATUS: PENDING REVIEW</div>
      </Card>
    </div>
  );

  return (
    <div className="tech-grid min-h-[calc(100vh-4rem)] py-10 px-4">
      <div className="max-w-2xl mx-auto fade-up">
        <div className="text-center mb-6">
          <div className="pill-cyan mx-auto w-fit mb-3"><Store className="w-3 h-3"/> DAFTAR SELLER</div>
          <h1 className="font-display text-3xl font-bold text-white">Buka Toko di tokoinduk.com</h1>
          <p className="text-slate-400 text-sm mt-2">Jangkau ribuan mitra SPPG & warung di seluruh Indonesia. Butuh review admin.</p>
        </div>
        <Card className="glass glow-border bg-transparent rounded-2xl p-6 sm:p-8">
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Nama Pemilik</Label>
                <Input data-testid="seller-name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="mt-1.5 bg-slate-900/60 border-slate-700 text-white h-11"/>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Email</Label>
                <Input data-testid="seller-email" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="mt-1.5 bg-slate-900/60 border-slate-700 text-white h-11"/>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">No. WhatsApp</Label>
                <Input data-testid="seller-phone" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} className="mt-1.5 bg-slate-900/60 border-slate-700 text-white h-11"/>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Nama Toko</Label>
                <Input data-testid="seller-shop" value={form.shop_name} onChange={e=>setForm({...form,shop_name:e.target.value})} className="mt-1.5 bg-slate-900/60 border-slate-700 text-white h-11"/>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Kota / Kabupaten</Label>
                <div className="mt-1.5">
                  <CityAutocomplete testid="seller-city" value={form.city} onChange={v => setForm({...form, city: v})} placeholder="Karawang, Bandung, Surabaya…"/>
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Kategori Produk</Label>
                <Select value={form.category} onValueChange={v=>setForm({...form,category:v})}>
                  <SelectTrigger data-testid="seller-category" className="mt-1.5 bg-slate-900/60 border-slate-700 text-white h-11">
                    <SelectValue placeholder="Pilih kategori"/>
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-white">
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Upload KTP</Label>
                <label className="mt-1.5 flex items-center gap-2 bg-slate-900/60 border border-dashed border-slate-700 rounded-md p-3 cursor-pointer hover:border-cyan-500/50">
                  <Upload className="w-4 h-4 text-cyan-400"/>
                  <span className="text-xs text-slate-300 truncate">{ktp?.name || "Pilih file (JPG/PNG/PDF)"}</span>
                  <input data-testid="seller-ktp" type="file" accept="image/*,.pdf" onChange={e => setKtp(e.target.files?.[0] || null)} className="hidden"/>
                </label>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Upload NIB / SIUP <span className="text-slate-500 lowercase">(opsional)</span></Label>
                <label className="mt-1.5 flex items-center gap-2 bg-slate-900/60 border border-dashed border-slate-700 rounded-md p-3 cursor-pointer hover:border-cyan-500/50">
                  <Upload className="w-4 h-4 text-cyan-400"/>
                  <span className="text-xs text-slate-300 truncate">{nib?.name || "Pilih file"}</span>
                  <input data-testid="seller-nib" type="file" accept="image/*,.pdf" onChange={e => setNib(e.target.files?.[0] || null)} className="hidden"/>
                </label>
              </div>
            </div>

            <Button data-testid="seller-submit" type="submit" disabled={loading} className="w-full h-11 bg-emerald-400 hover:bg-emerald-300 text-slate-950 font-bold">
              {loading ? "Mengirim..." : "Kirim untuk Review"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
